import React, { useState } from 'react';
import { defineMessages } from 'react-intl';
import PropTypes from 'prop-types';
import Modal from 'react-modal';
import { useDispatch, useSelector } from 'react-redux';
import CCEvents, {
  EventNames,
  EventPropertyKeys,
  EventPropertyValues,
} from '@companycam/companycam-events';

import { ccIntl } from 'helpers/intl-helper';
import { userHasPermission, MANAGE_ACCOUNT } from 'helpers/permissions';
import { Steven } from 'helpers/steven';
import { createTag } from 'actions/tags';
import TagSelect from 'components/core/TagSelect';
import { ActionLink } from 'components/shared';
import RecordableTypes from 'constants/recordableTypes';
import { Button } from '@companycam/slab-web';
import { debounce } from 'lodash';

defineMessages({
  modalTitle: {
    id: 'Activity.TagRecordablesModal.modalTitle',
    defaultMessage: 'Select Tags',
    description: 'modal title',
  },
  manageTags: {
    id: 'Activity.TagRecordablesModal.manageTags',
    defaultMessage: "Manage Your Company's Tags",
    description: 'manage company tags button',
  },
  tagPhotos: {
    id: 'Activity.TagRecordablesModal.tagPhotos',
    defaultMessage: 'Tag Photos',
    description: 'tag photos button',
  },
  tagSectionPhotos: {
    id: 'Activity.TagRecordablesModal.tagSectionPhotos',
    defaultMessage: 'Tag all photos in a section',
    description: 'tag section photos button',
  },
  noResultsText: {
    id: 'Activity.TagRecordablesModal.noResultsText',
    defaultMessage: 'No results found',
    description: 'no results found message',
  },
  selectPlaceholder: {
    id: 'Activity.TagRecordablesModal.selectPlaceholder',
    defaultMessage: 'Find tags…',
    description: 'placeholder (select only)',
  },
  selectOrCreatePlaceholder: {
    id: 'Activity.TagRecordablesModal.selectOrCreatePlaceholder',
    defaultMessage: 'Find tags or create them…',
    description: 'placeholder (creation allowed)',
  },
});

const { formatMessage } = ccIntl;
const tagPhotos = formatMessage({
  id: 'Activity.TagRecordablesModal.tagPhotos',
});
const tagSectionPhotos = formatMessage({
  id: 'Activity.TagRecordablesModal.tagSectionPhotos',
});
const noResultsText = formatMessage({
  id: 'Activity.TagRecordablesModal.noResultsText',
});
const selectPlaceholder = formatMessage({
  id: 'Activity.TagRecordablesModal.selectPlaceholder',
});
const selectOrCreatePlaceholder = formatMessage({
  id: 'Activity.TagRecordablesModal.selectOrCreatePlaceholder',
});
const modalTitle = formatMessage({
  id: 'Activity.TagRecordablesModal.modalTitle',
});
const manageTags = formatMessage({
  id: 'Activity.TagRecordablesModal.manageTags',
});

const eventPropertyValueMap = {
  [RecordableTypes.PHOTO]: EventPropertyValues.PHOTO,
  [RecordableTypes.VIDEO]: EventPropertyValues.VIDEO,
  [RecordableTypes.PROJECT]: EventPropertyValues.PROJECT,
};

const TagsModal = (props) => {
  const { originalTags, taggingSectionPhotos, onRequestClose, buttonTitle } =
    props;

  const [haveModifiedTags, setHaveModifiedTags] = useState(false);
  const [selectedTags, setSelectedTags] = useState([]);

  const dispatch = useDispatch();
  const currentUser = useSelector((state) => state.user);

  const saveTags = async () => {
    const { handleTagSave, alterTags, nodeIds, action, recordableType } = props;

    if (!haveModifiedTags) {
      onRequestClose();
    }

    const valuesToCreate = selectedTags
      .filter((tag) => !!tag.__isNew__)
      .map((tag) => tag.value);
    await Promise.all(
      valuesToCreate.map((value) =>
        dispatch(createTag({ display_value: value })).then((tag) => {
          Steven.entityCrud({
            entity_id: tag.id.toString(),
            entity_type: 'tag',
            action: 'created',
            source: recordableType,
          });
        }),
      ),
    );

    const nextValues = selectedTags.map(({ display_value, value, __isNew__ }) =>
      __isNew__ ? value : display_value,
    );

    if (taggingSectionPhotos) {
      handleTagSave(nextValues);
    } else {
      await alterTags({
        recordableIds: nodeIds,
        tags: nextValues,
        action,
      });
    }

    CCEvents.logEvent(EventNames.TAG, {
      [EventPropertyKeys.TAGGABLE]: eventPropertyValueMap[recordableType],
    });

    onRequestClose();
  };

  const onSelectionChange = (nextSelectedTags) => {
    setHaveModifiedTags(true);
    setSelectedTags(nextSelectedTags || []);

    // Log steven event when a tag is added
    if (nextSelectedTags.length > selectedTags.length) {
      Steven.search({
        search_action: 'search_result_clicked',
        search_result_type: 'tags',
      });
    }
  };

  const onKeyDown = debounce((_) => {
    Steven.search({
      search_action: 'search_performed',
      search_performed_location: 'add_tags',
    });
  }, 1000);

  const selectValue = haveModifiedTags
    ? selectedTags
    : originalTags.map((tag) => ({ display_value: tag.displayValue }));

  const hasManageTagsPermission = userHasPermission(
    currentUser,
    MANAGE_ACCOUNT,
  );

  const contentLabel = taggingSectionPhotos ? tagSectionPhotos : tagPhotos;

  return (
    <Modal
      isOpen
      ariaHideApp={false}
      onRequestClose={onRequestClose}
      contentLabel={contentLabel}
      overlayClassName="modal"
      className="modal-content-wrap min-height"
    >
      <ActionLink
        className="modal-close-button"
        onClick={onRequestClose}
        data-testid="tags__modal-close-button"
      >
        <i className="mdi mdi-close" />
      </ActionLink>
      <h3 className="modal-title">{modalTitle}</h3>
      <TagSelect
        allowCreate={hasManageTagsPermission}
        autoFocus
        initialValue={selectValue}
        onChange={onSelectionChange}
        onKeyDown={onKeyDown}
        noOptionsMessage={noResultsText}
        placeholder={
          hasManageTagsPermission
            ? selectOrCreatePlaceholder
            : selectPlaceholder
        }
      />
      <div className="btn-container full">
        <Button
          data-testid="tags__modal-save-button"
          color="primary"
          onClick={saveTags}
        >
          {buttonTitle}
        </Button>
        {hasManageTagsPermission ? (
          <Button href="/tags" data-turbo="false">
            {manageTags}
          </Button>
        ) : null}
      </div>
    </Modal>
  );
};

TagsModal.propTypes = {
  originalTags: PropTypes.array, // GraphQL tag objects
  nodeIds: PropTypes.array.isRequired,
  buttonTitle: PropTypes.string,
  onRequestClose: PropTypes.func.isRequired,
  action: PropTypes.oneOf(['ADD', 'REPLACE']).isRequired,
  recordableType: PropTypes.oneOf([
    RecordableTypes.PHOTO,
    RecordableTypes.VIDEO,
  ]).isRequired,
  taggingSectionPhotos: PropTypes.bool,
  handleTagSave: PropTypes.func,
  alterTags: PropTypes.func.isRequired,
};

TagsModal.defaultProps = {
  originalTags: [],
  buttonTitle: tagPhotos,
  taggingSectionPhotos: false,
  handleTagSave: () => {},
};

export default TagsModal;
